import React, { useContext, useState, useEffect } from 'react';
import { NetworkSetupTab } from './NetworkSetupTab';
import { ProductsTab } from './ProductsTab';
import { ConfigurationProvider, useConfigurationContext } from './Context/ConfigurationContext';
import { AvailabilityTab } from './AvailabilityTab';
import { toast } from 'react-toastify';
import closeIcon from '../../../../src/assests/img/close-icon.svg';
import { AppContext } from 'Context/AppContext';
import Button from 'Components/DesignSystem/Button/Button';

type TabType = 'Network Setup' | 'Products' | 'Availability' | 'Related Products' | 'Orders' | 'Substitutes';

type ConfigurationPanelProps = {
    id?: string;
    setShowConfigurationPanel: (show: boolean) => void;
    callback?: () => void;
};

const CONFIGURATION_TABS: TabType[] = [
    'Network Setup',
    'Products',
    'Availability',
    'Related Products',
    'Orders',
    'Substitutes',
];

export const ConfigurationPanel = ({
    id = 'configuration-panel',
    setShowConfigurationPanel,
    callback
}: ConfigurationPanelProps) => {
    return (
        <ConfigurationProvider>
            <ConfigurationPanelContent
                id={id}
                setShowConfigurationPanel={setShowConfigurationPanel}
                callback={callback}
            />
        </ConfigurationProvider>
    );
};

type ConfigurationPanelContentProps = {
    id: string;
    setShowConfigurationPanel: (show: boolean) => void;
    callback?: () => void;
};

const ConfigurationPanelContent = ({
    id,
    setShowConfigurationPanel
}: ConfigurationPanelContentProps) => {
    const [activeTab, setActiveTab] = useState<TabType>('Network Setup');
    const { isEditMode, setIsEditMode, saveConfiguration, currentConfig, cancelEdit } = useConfigurationContext();
    const appData = useContext(AppContext);

    const importType = currentConfig?.network_setup?.import_type;

    const isTabEnabled = (tab: TabType): boolean => {
        if (tab !== 'Network Setup' && importType == null) { return false }
        switch (tab) {
            case 'Network Setup':
                return true;
            case 'Products':
                return true;
            case 'Availability':
                return true;
            case 'Related Products':
            case 'Orders':
            case 'Substitutes':
                return false;
            default:
                return false;
        }
    };

    useEffect(() => {
        if (activeTab === 'Products' && importType == null) {
            setActiveTab('Network Setup');
        }
    }, [importType, activeTab]);

    const handleCancel = () => {
        cancelEdit();
        setShowConfigurationPanel(false);
    };

    const handleSave = async () => {
        const success = await saveConfiguration();
        if (success) {
            setShowConfigurationPanel(false);
        }
    };

    const handleTabChange = (tab: TabType) => {
        if (isTabEnabled(tab)) {
            setActiveTab(tab);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'Network Setup':
                return <NetworkSetupTab id={`${id}-network-setup`} />;
            case 'Products':
                return <ProductsTab id={`${id}-products`} />;
            case 'Availability':
                return <AvailabilityTab id={`${id}-availability`} />;
            default:
                return null;
        }
    };

    return (
        <div className="ps-0 form-header tw-overflow-x-hidden" data-testid={id}>
            <div
                className="connection-container d-flex justify-content-between pe-3 ps-3"
                data-testid={`${id}-header`}
            >
                <span
                    className="connection-text align-self-center fs-5 mb-0"
                    data-testid={`${id}-header-title`}
                >
                    Configuration
                </span>
                {isEditMode && (
                    <div
                        className="d-flex gap-2 align-self-center"
                        data-testid={`${id}-edit-actions`}
                    >
                        <Button
                            className="btn btn-outline-secondary"
                            onClick={handleCancel}
                            data-testid={`${id}-cancel-btn`}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            className=""
                            onClick={handleSave}
                            data-testid={`${id}-save-btn`}
                        >
                            Save
                        </Button>
                    </div>
                )}
                {!isEditMode && (
                    <div
                        className="d-flex gap-2 align-items-center"
                        data-testid={`${id}-view-actions`}
                    >
                        <Button
                            variant="secondary"
                            data-testid={`${id}-edit-btn`}
                            onClick={() => {
                                if (appData.pm.checkPermission("connectionmanager_read")) {
                                    setIsEditMode(true);
                                } else {
                                    toast.info("You do not have permission to update the configuration.");
                                }
                            }}
                        >
                            Edit
                        </Button>
                        <button
                            type="button"
                            className="border-0 btn fw fw-light shadow-none p-0"
                            onClick={() => {
                                setIsEditMode(false);
                                setShowConfigurationPanel(false);
                            }}
                            data-testid={`${id}-close-btn`}
                        >
                            <img
                                src={closeIcon}
                                alt="Close icon"
                                data-testid={`${id}-close-icon`}
                            />
                        </button>
                    </div>
                )}
            </div>

            <div
                className="ps-3 tw-overflow-x-auto tw-overflow-y-hidden [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                style={{ flex: '0 0 auto', borderBottom: '1px solid #e0e0e0' }}
                data-testid={`${id}-tabs`}
            >
                <div className="cms-multi-tab-main">
                    <div className="cms-multi-tab-link">
                        {CONFIGURATION_TABS.map((tab, index) => {
                            const enabled = isTabEnabled(tab);
                            const tabId = tab.toLowerCase().replace(/\s+/g, '-');
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`btn text-sm cms-tab-link-item no-focus py-2 px-0 tw-whitespace-nowrap ${activeTab === tab ? 'active' : ''
                                        } ${!enabled ? 'disabled tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                                    onClick={() => handleTabChange(tab)}
                                    disabled={!enabled}
                                    data-testid={`${id}-tab-${tabId}`}
                                >
                                    {tab}
                                    {['Products', 'Availability'].includes(tab) && <span className="tw-text-red-500 tw-ml-1">*</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div
                className="tw-px-3 tw-py-4 tw-overflow-x-hidden"
                style={{ flex: '1 1 auto', overflowY: 'auto' }}
                data-testid={`${id}-content`}
            >
                {renderTabContent()}
            </div>
        </div>
    );
};

export default ConfigurationPanel;